import React, { useEffect, useMemo, useRef, useState } from 'react';

import usePageMetadata from '../../hooks/usePageMetadata';
import { convertHardness } from '../../services/hardnessService';
import './HardnessComparison.css';
import { useUnits } from '../../units/UnitContext';
import { formatUnitValue, unitLabel } from '../../units/unitConversions';
import { useUnitDrafts } from '../../units/useUnitDrafts';

const REQUEST_DELAY_MS = 200;
const PAGE_TITLE = 'Hardness Comparison for Machining | ASiJoLab';
const PAGE_DESCRIPTION =
  'Compare approximate Rm tensile strength, Brinell, Vickers and Rockwell hardness values for steel and hardmetal machining orientation.';
const STEEL_SCALES = ['MPa_Rm', 'HBW', 'HV', 'HRC', 'HRB', 'HRA'];
const HARDMETAL_SCALES = ['HRA', 'HV'];
const SCALE_DETAILS = {
  MPa_Rm: { label: 'Rm', option: 'MPa (Rm)', description: 'Approx. tensile strength', unit: 'MPa' },
  HBW: { label: 'HBW', option: 'HBW', description: 'Brinell hardness', unit: 'HBW' },
  HV: { label: 'HV', option: 'HV', description: 'Vickers hardness', unit: 'HV' },
  HRC: { label: 'HRC', option: 'HRC', description: 'Rockwell C', unit: 'HRC' },
  HRB: { label: 'HRB', option: 'HRB', description: 'Rockwell B', unit: 'HRB' },
  HRA: { label: 'HRA', option: 'HRA', description: 'Rockwell A', unit: 'HRA' },
};
const STATUS_TEXT = {
  outside_range: 'Outside recommended range',
  not_applicable: 'Not applicable',
  conversion_not_recommended: 'Conversion not recommended',
};

const isNumericResult = (result) =>
  (result?.status === 'value' || result?.status === 'extended_indicative') && Number.isFinite(result?.value);

const ResultCard = ({ scale, result, enteredScale, unitSystem }) => {
  const details = SCALE_DETAILS[scale];
  const entered = scale === enteredScale && isNumericResult(result);
  const indicative = result?.status === 'extended_indicative';
  return (
    <article className={`hardness-result-card${entered ? ' is-entered' : ''}`}>
      <div className="hardness-result-heading">
        <div><h3>{details.label}</h3><p>{details.description}</p></div>
        {entered && <span className="hardness-result-badge">Entered value</span>}
        {indicative && <span className="hardness-result-badge is-indicative">Extended / indicative</span>}
      </div>
      {isNumericResult(result) ? (
        <p className="hardness-result-value">{!entered && '≈ '}{scale === 'MPa_Rm' ? formatUnitValue(result.value, 'mpa', unitSystem) : result.value} {scale === 'MPa_Rm' ? unitLabel('mpa', unitSystem) : details.unit}</p>
      ) : (
        <p className="hardness-result-status">{STATUS_TEXT[result?.status] || 'Not available'}</p>
      )}
      {!entered && isNumericResult(result) && <p className="hardness-result-kind">Approximate</p>}
    </article>
  );
};

const HardnessComparison = () => {
  const { unitSystem } = useUnits();
  const [unitInput, setUnitInput] = useState({ value: '' });
  const inputValue = unitInput.value;
  const setInputValue = value => setUnitInput({ value });
  const [scale, setScale] = useState('MPa_Rm');
  const canonicalStrength = useUnitDrafts(unitInput, setUnitInput, scale === 'MPa_Rm' ? { value: 'mpa' } : {}, unitSystem);
  const [materialModel, setMaterialModel] = useState('steel');
  const [includeExtended, setIncludeExtended] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const requestVersion = useRef(0);
  usePageMetadata(PAGE_TITLE, PAGE_DESCRIPTION);

  const availableScales = materialModel === 'hardmetal' ? HARDMETAL_SCALES : STEEL_SCALES;
  const visibleResultScales = useMemo(
    () => result?.input?.materialModel === 'hardmetal' ? HARDMETAL_SCALES : STEEL_SCALES,
    [result]
  );

  useEffect(() => {
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    const trimmedValue = inputValue.trim();
    if (trimmedValue === '') {
      setLoading(false); setResult(null); setMessage(null);
      return undefined;
    }
    const numericValue = Number(trimmedValue);
    if (!Number.isFinite(numericValue)) {
      setLoading(false); setResult(null);
      setMessage({ type: 'invalid', text: 'Enter a finite numeric value.' });
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setMessage(null);
    const timer = window.setTimeout(async () => {
      try {
        const loaded = await convertHardness(
          { value: scale === 'MPa_Rm' ? canonicalStrength('value', inputValue, 'mpa') : numericValue, scale, materialModel, includeExtended },
          { signal: controller.signal }
        );
        if (requestVersion.current === version) { setResult(loaded); setMessage(null); }
      } catch (error) {
        if (requestVersion.current !== version || error?.code === 'ERR_CANCELED') return;
        setResult(null);
        if (error?.response?.status === 400) {
          setMessage({ type: 'invalid', text: error?.response?.data?.message || 'Check the entered value and scale.' });
        } else {
          setMessage({ type: 'error', text: 'The conversion could not be loaded. Check your connection and try again.' });
        }
      } finally {
        if (requestVersion.current === version) setLoading(false);
      }
    }, REQUEST_DELAY_MS);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [canonicalStrength, includeExtended, inputValue, materialModel, scale, unitSystem]);

  const handleMaterialModelChange = (event) => {
    const nextModel = event.target.value;
    setMaterialModel(nextModel);
    if (nextModel === 'hardmetal') {
      setIncludeExtended(false);
      if (!HARDMETAL_SCALES.includes(scale)) setScale('HRA');
    }
  };

  const outsideInput = result?.inputStatus === 'outside_range';
  const indicative = result?.inputStatus === 'extended_indicative';
  return (
    <main className="hardness-comparison">
      <header className="hardness-page-header">
        <p>Materials · Practical conversion</p>
        <h1>Hardness comparison for machining</h1>
        <span>Enter one known value to compare approximate equivalents across useful engineering scales.</span>
      </header>

      <section className="hardness-converter" aria-labelledby="hardness-converter-title">
        <header className="hardness-section-heading">
          <p>Automatic conversion</p><h2 id="hardness-converter-title">Hardness / Strength</h2>
        </header>
        <div className="hardness-input-row">
          <label htmlFor="hardness-value"><span>Known value</span>
            <input id="hardness-value" type="number" inputMode="decimal" step="any" autoComplete="off"
              value={inputValue} onChange={(event) => setInputValue(event.target.value)}
              aria-describedby={message || outsideInput ? 'hardness-input-message' : 'hardness-results'} />
          </label>
          <label htmlFor="hardness-scale"><span>Scale</span>
            <select id="hardness-scale" value={scale} onChange={(event) => setScale(event.target.value)}>
              {availableScales.map((scaleId) => <option key={scaleId} value={scaleId}>{scaleId === 'MPa_Rm' ? `${unitLabel('mpa', unitSystem)} (Rm)` : SCALE_DETAILS[scaleId].option}</option>)}
            </select>
          </label>
        </div>

        <details className="hardness-advanced">
          <summary>Advanced options</summary>
          <div className="hardness-advanced-content">
            <label htmlFor="hardness-material-model"><span>Material type</span>
              <select id="hardness-material-model" value={materialModel} onChange={handleMaterialModelChange}>
                <option value="steel">Steel</option>
                <option value="hardmetal">Hardmetal / cemented carbide</option>
              </select>
            </label>
            {materialModel === 'steel' && (
              <label className="hardness-checkbox" htmlFor="hardness-extended">
                <input id="hardness-extended" type="checkbox" checked={includeExtended}
                  onChange={(event) => setIncludeExtended(event.target.checked)} />
                <span>Enable extended high-hardness range</span>
              </label>
            )}
          </div>
        </details>

        <div className="hardness-feedback" aria-live="polite">
          {loading && <p className="hardness-loading">Updating approximate equivalents…</p>}
          {message && <p id="hardness-input-message" className={`hardness-message is-${message.type}`}
            role={message.type === 'error' ? 'alert' : undefined}>{message.text}</p>}
          {outsideInput && !message && <p id="hardness-input-message" className="hardness-message is-range">
            The entered value is outside the supported range for this scale and material type.
          </p>}
        </div>
      </section>

      <section id="hardness-results" className="hardness-results" aria-labelledby="hardness-results-title" aria-busy={loading}>
        <div className="hardness-results-header">
          <div><p>Comparison</p><h2 id="hardness-results-title">Approximate equivalents</h2></div>
          {loading && result && <span>Updating…</span>}
        </div>
        {result ? <>
          {indicative && <aside className="hardness-indicative-note"><strong>Extended / indicative</strong><span>
            {result.input.materialModel === 'hardmetal'
              ? 'This hardmetal value is beyond the defined HRA test-method range and is shown only as an estimated orientation.'
              : 'This value is outside the main standard steel conversion range and is provided only as an approximate orientation.'}
          </span></aside>}
          <div className="hardness-result-grid">
            {visibleResultScales.map((scaleId) => <ResultCard key={scaleId} scale={scaleId}
              result={result.results?.[scaleId]} enteredScale={result.input.scale} unitSystem={unitSystem} />)}
          </div>
        </> : <p className="hardness-empty-state">Enter a known hardness or Rm value to see approximate equivalents.</p>}
      </section>

      <section className="hardness-explanation" aria-labelledby="hardness-explanation-title">
        <p>How to read this comparison</p>
        <h2 id="hardness-explanation-title">Approximate orientation, not certification</h2>
        <div className="hardness-explanation-grid">
          <p>Hardness conversions are approximate, and every scale has its own useful range.</p>
          <p>{unitLabel('mpa', unitSystem)} represents approximate Rm—ultimate tensile strength, not a hardness scale.</p>
          <p>Very hard materials are better described by hardness than by extrapolated tensile strength.</p>
          <p>Material type and microstructure can change the relationship between scales.</p>
        </div>
        <p className="hardness-verification-note">For exact engineering verification, direct testing and the relevant standard take precedence.</p>
      </section>
    </main>
  );
};

export default HardnessComparison;
